/**
 * SupportHoursAdminPanel — full control over the Night Owl banner.
 *
 * Mode:
 *   · auto        → banner auto-shows between start_hour and end_hour CT
 *   · always_on   → banner always shows (holidays, closures, etc.)
 *   · always_off  → banner never shows
 *
 * All copy (pill label, headline, body paragraph, CTA button text) and the
 * schedule window are fully editable. Live preview at the top so admins can
 * see exactly what players will see.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Moon, Save, RotateCcw } from 'lucide-react';
import NightOwlBanner from '../../components/NightOwlBanner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => {
    const label = i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`;
    return { value: i, label };
});

const DEFAULTS = {
    mode: 'auto',
    start_hour: 22,
    end_hour: 8,
    pill_label: 'Night Owl Hours',
    title: 'Our team clocked out — but the tables never sleep.',
    body: (
        'Messages sent overnight will be answered first thing at 8 AM CT. '
        + 'Need chips right now? One-Tap Load is fully automated — pay via PayPal '
        + 'and your credits post instantly once your receipt clears. '
        + 'Sweet dreams, and good luck. 🌙'
    ),
    cta_text: 'Auto-Load Now',
};

export const SupportHoursAdminPanel = () => {
    const [cfg, setCfg] = useState(null);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [previewKey, setPreviewKey] = useState(0);

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/support-hours`);
            setCfg(data);
            setForm({
                mode: data.mode,
                start_hour: data.start_hour,
                end_hour: data.end_hour,
                pill_label: data.pill_label,
                title: data.title,
                body: data.body,
                cta_text: data.cta_text,
            });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load support hours config');
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        if (!form) return;
        setSaving(true);
        try {
            const { data } = await axios.put(`${API}/admin/support-hours`, form, { withCredentials: true });
            setCfg(data);
            setPreviewKey((k) => k + 1);
            toast.success('Support hours saved');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally { setSaving(false); }
    };

    const resetDefaults = () => {
        setForm({ ...DEFAULTS });
    };

    if (!form || !cfg) {
        return (
            <div className="rounded-2xl border border-white/10 bg-[#0f0f14] p-4 text-sm text-white/60" data-testid="support-hours-loading">
                Loading support-hours config…
            </div>
        );
    }

    const previewNote = cfg.is_active
        ? `⚠ Banner is currently LIVE for players (server hour: ${cfg.server_ct_hour} CT).`
        : `Banner is currently HIDDEN (server hour: ${cfg.server_ct_hour} CT). Preview below shows what players will see when it activates.`;

    return (
        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#0f0f14] to-[#050508] p-4 md:p-5 shadow-[0_0_25px_rgba(0,0,0,0.4)]" data-testid="support-hours-panel">
            <div className="flex items-center gap-2 mb-3">
                <Moon className="w-5 h-5 text-[#FFD700]" />
                <h3 className="text-lg font-bold text-white">Support Hours & After-Hours Banner</h3>
            </div>
            <p className="text-xs text-[#8891A0] mb-4">
                Controls the &quot;Night Owl&quot; banner on the Casino Floor. Use <b>always_on</b> during
                holidays / vacations to keep it up all day, or <b>always_off</b> to hide it entirely.
            </p>

            {/* Mode + schedule */}
            <div className="grid md:grid-cols-3 gap-3 mb-4">
                <div>
                    <label className="block text-[10px] uppercase tracking-wider text-[#8891A0] mb-1">Mode</label>
                    <select
                        value={form.mode}
                        onChange={(e) => setForm({ ...form, mode: e.target.value })}
                        className="w-full bg-black/60 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                        data-testid="support-hours-mode"
                    >
                        <option value="auto">Auto (schedule)</option>
                        <option value="always_on">Always ON (holiday / closure)</option>
                        <option value="always_off">Always OFF</option>
                    </select>
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-wider text-[#8891A0] mb-1">Start (CT)</label>
                    <select
                        value={form.start_hour}
                        onChange={(e) => setForm({ ...form, start_hour: Number(e.target.value) })}
                        disabled={form.mode !== 'auto'}
                        className="w-full bg-black/60 border border-white/20 rounded-lg px-3 py-2 text-white text-sm disabled:opacity-40"
                        data-testid="support-hours-start"
                    >
                        {HOUR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-wider text-[#8891A0] mb-1">End (CT)</label>
                    <select
                        value={form.end_hour}
                        onChange={(e) => setForm({ ...form, end_hour: Number(e.target.value) })}
                        disabled={form.mode !== 'auto'}
                        className="w-full bg-black/60 border border-white/20 rounded-lg px-3 py-2 text-white text-sm disabled:opacity-40"
                        data-testid="support-hours-end"
                    >
                        {HOUR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
            </div>

            {/* Copy fields */}
            <div className="space-y-3 mb-4">
                <div>
                    <label className="block text-[10px] uppercase tracking-wider text-[#8891A0] mb-1">Pill Label (small tag at top)</label>
                    <input
                        type="text" maxLength={40}
                        value={form.pill_label}
                        onChange={(e) => setForm({ ...form, pill_label: e.target.value })}
                        className="w-full bg-black/60 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                        data-testid="support-hours-pill"
                    />
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-wider text-[#8891A0] mb-1">Headline</label>
                    <input
                        type="text" maxLength={200}
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        className="w-full bg-black/60 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                        data-testid="support-hours-title"
                    />
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-wider text-[#8891A0] mb-1">Body paragraph (supports line breaks)</label>
                    <textarea
                        maxLength={1000} rows={4}
                        value={form.body}
                        onChange={(e) => setForm({ ...form, body: e.target.value })}
                        className="w-full bg-black/60 border border-white/20 rounded-lg px-3 py-2 text-white text-sm resize-y"
                        data-testid="support-hours-body"
                    />
                    <div className="text-[10px] text-[#8891A0] mt-0.5 text-right">{form.body.length}/1000</div>
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-wider text-[#8891A0] mb-1">CTA button text</label>
                    <input
                        type="text" maxLength={40}
                        value={form.cta_text}
                        onChange={(e) => setForm({ ...form, cta_text: e.target.value })}
                        className="w-full bg-black/60 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                        data-testid="support-hours-cta"
                    />
                </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
                <button
                    onClick={save} disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-[#FFD700] text-black rounded-lg text-sm font-bold hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="support-hours-save"
                >
                    <Save className="w-4 h-4" />{saving ? 'Saving…' : 'Save Changes'}
                </button>
                <button
                    onClick={resetDefaults}
                    className="flex items-center gap-2 px-4 py-2 bg-black/60 border border-white/20 rounded-lg text-sm text-white hover:border-white/40"
                    data-testid="support-hours-reset"
                >
                    <RotateCcw className="w-4 h-4" />Restore Defaults
                </button>
                <a
                    href="/lobby?nightowl=1" target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-black/60 border border-[#FFD700]/40 rounded-lg text-sm text-[#FFD700] hover:border-[#FFD700]/80"
                    data-testid="support-hours-preview-lobby"
                >
                    Preview in Lobby →
                </a>
            </div>

            {/* Live preview using the actual saved config */}
            <div className="border-t border-white/10 pt-4">
                <div className="text-[10px] uppercase tracking-wider text-[#8891A0] mb-2">Live preview (saved config)</div>
                <div className="text-xs text-[#c8b8e8] mb-3">{previewNote}</div>
                {/* Force NightOwlBanner to remount on save + always-render via ?nightowl=1
                    in the surrounding query — we use a wrapper URL param trick with key
                    to force re-fetch after save, and the ?nightowl=1 flag on the wrapper
                    div uses history state (falls back to just showing the banner). */}
                <PreviewBanner key={previewKey} />
            </div>
        </div>
    );
};

// Renders the same NightOwlBanner but forced visible via URL search param.
const PreviewBanner = () => {
    // Temporarily set ?nightowl=1 on the URL just for this mount so the banner
    // component's forceOverride flag returns true. Restored on unmount.
    useEffect(() => {
        const url = new URL(window.location.href);
        const had = url.searchParams.get('nightowl');
        url.searchParams.set('nightowl', '1');
        window.history.replaceState({}, '', url.toString());
        return () => {
            const restoreUrl = new URL(window.location.href);
            if (had == null) restoreUrl.searchParams.delete('nightowl');
            else restoreUrl.searchParams.set('nightowl', had);
            window.history.replaceState({}, '', restoreUrl.toString());
        };
    }, []);
    return <NightOwlBanner />;
};

export default SupportHoursAdminPanel;
